import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * Decode HTML character references (e.g. `&euro;` → `€`) from a string.
 * shop.money_format is sometimes stored with HTML entities; using it verbatim
 * via textContent renders the literal entity text instead of the symbol.
 * @param {string} str
 * @returns {string}
 */
function decodeEntities(str) {
  const el = document.createElement('textarea');
  el.innerHTML = str;
  return el.value;
}

/**
 * Persists the active panel ID across section re-renders (AJAX morphing).
 * Keyed by section ID.
 * @type {Map<string, string>}
 */
const savedPanels = new Map();

/**
 * Manages drill-down sub-panel navigation and dual-thumb price slider
 * inside the collection filter drawer.
 *
 * HTML contract:
 * - `[data-open-panel="<id>"]` on root row buttons → opens the named panel
 * - `[data-close-panel]` on back buttons inside panels → returns to root
 * - `[data-price-slider]` on the slider wrapper → bound for thumb ↔ hidden-input sync
 * - `data-section-id` on this element → used to persist panel state across re-renders
 *
 * Morph safety:
 * - A MutationObserver watches for morph resetting the `is-open` class on the
 *   active panel (morph runs synchronously; the observer fires in a microtask
 *   before the next paint, so no visual flicker occurs).
 * - The savedPanels map handles the case where the element is fully replaced
 *   rather than morphed in-place (connectedCallback fires on the new instance).
 *
 * @extends {Component}
 */
class FacetDrawerPanels extends Component {
  /** @type {string | null} */
  #activePanelId = null;

  /** @type {HTMLElement | null} */
  #activeTrigger = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#onClick);
    this.addEventListener('input', this.#onSliderInput);
    this.addEventListener('keydown', this.#onKeyDown);

    // Watch for morph resetting panel open state.
    // morph() runs synchronously; MutationObserver callbacks fire in a
    // microtask after all mutations complete but before the browser paints.
      this.#observer = new MutationObserver(this.#onMutation);
      this.#observer.observe(this, {
        subtree: true,
        attributes: true,
        // 'class'  → re-open active panel if morph removes is-open
        // 'value'  → sync range input property from attribute when morph resets slider
        attributeFilter: ['class', 'value'],
      });

    // Restore panel state if this element was fully replaced (not morphed in-place).
    const sectionId = this.dataset.sectionId ?? '';
    if (sectionId && savedPanels.has(sectionId)) {
      const panelId = savedPanels.get(sectionId) ?? '';
      this.#openPanelById(panelId, null, false);
    }

    // Paint initial price slider fills
    this.#syncAllSliders();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('input', this.#onSliderInput);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  // ─────────────────────────────────────────────── Event delegation ─────────

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const openBtn = target.closest('[data-open-panel]');
    if (openBtn instanceof HTMLElement) {
      this.#openPanelById(openBtn.dataset.openPanel ?? '', openBtn, true);
      return;
    }

    const closeBtn = target.closest('[data-close-panel]');
    if (closeBtn) {
      this.#closeActivePanel();
      return;
    }

    // The engine's `clearFilter` (bound directly on the button, so it runs before
    // this delegated handler) blanks *every* input inside `price-facet-component`,
    // including the two range thumbs. A range input with `value=""` snaps to the
    // track middle. Reset the thumbs back to the full range so the UI matches the
    // now-cleared (no price) state instead of getting stuck in the middle.
    const clearBtn = target.closest('.facets-drawer-panels__clear-btn');
    if (clearBtn) {
      const slider = clearBtn.closest('.facets-drawer-panels__panel')?.querySelector('[data-price-slider]');
      if (slider instanceof HTMLElement) this.#resetSlider(slider);
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.#activePanelId) return;
    // Swallow the Escape so dialog-component does not also close the drawer
    event.stopPropagation();
    this.#closeActivePanel();
  };

  /**
   * Fires continuously while a thumb is dragged (and per keyboard step). Repaints
   * the fill/labels, clamps min ≤ max live, and — crucially — mirrors the current
   * thumb positions into the hidden `price-facet-component` text inputs.
   *
   * Why mirror on every `input` instead of committing on `change`:
   * The skeleton wires `on:change="/updatePriceFilterAndResults"` as a single
   * `document`-level listener in the CAPTURE phase (see component.js). Because the
   * range thumbs live inside `price-facet-component`, their native `change` (on
   * release) is caught by that document delegate — which runs before any listener
   * we could add on this element — and the engine reads the hidden text inputs.
   * So the only reliable way to make the engine commit the RIGHT value is to keep
   * those text inputs fresh throughout the drag; the engine then reads correct
   * values on release and the morph keeps the thumb where the user left it.
   * @param {Event} event
   */
  #onSliderInput = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.classList.contains('facets-price-slider__thumb')) return;

    const sliderEl = event.target.closest('[data-price-slider]');
    if (!(sliderEl instanceof HTMLElement)) return;

    this.#raiseActiveThumb(sliderEl, event.target);
    this.#syncSliderUI(sliderEl, event.target);
    this.#syncSliderToInputs(sliderEl);
  };

  // ──────────────────────────────────────────── MutationObserver guard ──────

  /**
   * Fires after morph mutates the subtree. Handles two concerns:
   *
   * 1. CLASS changes — if morph removed `is-open` from the active panel, restore it
   *    before the browser paints. The `!contains('is-open')` guard makes it
   *    idempotent: when we add the class back the re-fired callback is a no-op.
   *
   * 2. VALUE changes on range inputs — morph updates the `value` ATTRIBUTE but
   *    browsers do not propagate attribute changes to the `value` PROPERTY once the
   *    user has dragged the slider. We sync property ← attribute so the thumb
   *    visually moves to the server-rendered position (e.g. after a filter clear).
   *    Multiple mutations for the same slider (min + max thumbs) are deduped.
   *
   * @param {MutationRecord[]} mutations
   */
  #onMutation = (mutations) => {
    const panelId = this.#activePanelId;

    let panelReset = false;
    /** @type {Set<HTMLElement>} */
    const slidersToSync = new Set();

    for (const mutation of mutations) {
      if (mutation.attributeName === 'class') {
        if (
          panelId &&
          mutation.target instanceof HTMLElement &&
          mutation.target.id === panelId &&
          !mutation.target.classList.contains('is-open')
        ) {
          panelReset = true;
        }
      } else if (
        mutation.attributeName === 'value' &&
        mutation.target instanceof HTMLInputElement &&
        mutation.target.type === 'range'
      ) {
        // Sync the IDL value property from the newly-set content attribute
        const newVal = mutation.target.getAttribute('value');
        if (newVal !== null) mutation.target.value = newVal;

        const slider = mutation.target.closest('[data-price-slider]');
        if (slider instanceof HTMLElement) slidersToSync.add(slider);
      }
    }

    if (panelReset) {
      const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
      if (panel instanceof HTMLElement) {
        panel.classList.add('is-open');
        panel.removeAttribute('aria-hidden');
      }
      const root = this.querySelector('.facets-drawer-panels__root');
      if (root instanceof HTMLElement) {
        root.setAttribute('aria-hidden', 'true');
      }
    }

    for (const slider of slidersToSync) {
      this.#syncSliderUI(slider);
    }
  };

  // ──────────────────────────────────────────────── Panel navigation ────────

  /**
   * Opens a sub-panel by DOM id.
   * @param {string}           panelId
   * @param {HTMLElement|null} trigger     The root row button that opened it
   * @param {boolean}          focusBack   Whether to focus the back button
   */
  #openPanelById(panelId, trigger, focusBack) {
    if (!panelId) return;
    const panel = this.querySelector(`#${CSS.escape(panelId)}`);
    if (!(panel instanceof HTMLElement)) return;

    // Hide root from AT
    const root = this.querySelector('.facets-drawer-panels__root');
    if (root instanceof HTMLElement) root.setAttribute('aria-hidden', 'true');

    // Reveal panel
    panel.removeAttribute('aria-hidden');
    panel.classList.add('is-open');

    if (trigger) {
      this.#activeTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');
    }

    this.#activePanelId = panelId;

    // Persist for the case where the element is fully replaced by morph
    const sectionId = this.dataset.sectionId ?? '';
    if (sectionId) savedPanels.set(sectionId, panelId);

    if (focusBack) {
      const backBtn = panel.querySelector('[data-close-panel]');
      if (backBtn instanceof HTMLElement) {
        requestAnimationFrame(() => backBtn.focus());
      }
    }
  }

  /** Closes the active sub-panel and returns focus to the triggering root row. */
  #closeActivePanel() {
    const panelId = this.#activePanelId;
    if (!panelId) return;

    const panel = this.querySelector(`#${CSS.escape(panelId)}`);
    if (panel instanceof HTMLElement) {
      panel.setAttribute('aria-hidden', 'true');
      panel.classList.remove('is-open');
    }

    const root = this.querySelector('.facets-drawer-panels__root');
    if (root instanceof HTMLElement) root.removeAttribute('aria-hidden');

    const trigger = this.#activeTrigger;
    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus();
    }

    this.#activePanelId = null;
    this.#activeTrigger = null;

    const sectionId = this.dataset.sectionId ?? '';
    if (sectionId) savedPanels.delete(sectionId);
  }

  // ──────────────────────────────────────────────── Price slider ────────────

  /** Paint fill tracks for all sliders on connect or section morph. */
  #syncAllSliders() {
    this.querySelectorAll('[data-price-slider]').forEach((el) => {
      if (el instanceof HTMLElement) this.#syncSliderUI(el);
    });
  }

  /**
   * Update the fill track and live labels to reflect current thumb positions.
   * Also enforces min ≤ max by *blocking* the moving thumb at the other thumb's
   * value (the min thumb can never be dragged past the max thumb, and vice versa).
   * @param {HTMLElement}      sliderEl
   * @param {HTMLElement|null} [activeThumb] - The thumb currently being moved, if any
   */
  #syncSliderUI(sliderEl, activeThumb = null) {
    const thumbMin = /** @type {HTMLInputElement|null} */ (
      sliderEl.querySelector('.facets-price-slider__thumb--min')
    );
    const thumbMax = /** @type {HTMLInputElement|null} */ (
      sliderEl.querySelector('.facets-price-slider__thumb--max')
    );
    if (!thumbMin || !thumbMax) return;

    const rangeMax = parseFloat(sliderEl.dataset.rangeMax ?? '0') || 1;
    let minVal = parseFloat(thumbMin.value);
    let maxVal = parseFloat(thumbMax.value);

    // Enforce min ≤ max by clamping the moving thumb (never push the other one).
    if (minVal > maxVal) {
      const active = activeThumb ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
      if (active === thumbMax) {
        // Max thumb dragged below min → block it at min.
        maxVal = minVal;
        thumbMax.value = String(maxVal);
      } else {
        // Min thumb dragged above max (default) → block it at max.
        minVal = maxVal;
        thumbMin.value = String(minVal);
      }
    }

    // Update ARIA values (aria-valuenow in raw minor units; aria-valuetext carries
    // the formatted price for screen readers so the numeric unit is meaningful)
    thumbMin.setAttribute('aria-valuenow', String(Math.round(minVal)));
    thumbMax.setAttribute('aria-valuenow', String(Math.round(maxVal)));

    // Update fill track custom properties
    const fill = sliderEl.querySelector('.facets-price-slider__fill');
    if (fill instanceof HTMLElement) {
      const leftPct = (minVal / rangeMax) * 100;
      const rightPct = ((rangeMax - maxVal) / rangeMax) * 100;
      fill.style.setProperty('--slider-fill-left', `${leftPct}%`);
      fill.style.setProperty('--slider-fill-right', `${rightPct}%`);
    }

    // Update live price labels.
    // Decode HTML entities once and cache on the element — shop.money_format is
    // sometimes stored as "&euro;{{amount}}" and dataset gives back "&euro;" which
    // textContent would render literally instead of as the "€" symbol.
    if (!sliderEl.__moneyFormat) {
      sliderEl.__moneyFormat = decodeEntities(sliderEl.dataset.moneyFormat ?? '{{amount}}');
    }
    const moneyFormat = sliderEl.__moneyFormat;
    const currency = sliderEl.dataset.currency ?? 'USD';
    const labelMin = sliderEl.querySelector('.facets-price-slider__label--min');
    const labelMax = sliderEl.querySelector('.facets-price-slider__label--max');
    const minLabel = this.#toMoney(minVal, moneyFormat, currency);
    const maxLabel = this.#toMoney(maxVal, moneyFormat, currency);
    thumbMin.setAttribute('aria-valuetext', minLabel);
    thumbMax.setAttribute('aria-valuetext', maxLabel);
    if (labelMin) labelMin.textContent = minLabel;
    if (labelMax) labelMax.textContent = maxLabel;
  }

  /**
   * Format a slider value to a money string.
   * Slider values are raw Shopify minor units (e.g. 150000 for €1500).
   * formatMoney already expects minor units, so we pass the value directly.
   * @param {number} value   - minor-unit integer from the range input
   * @param {string} moneyFormat
   * @param {string} currency
   * @returns {string}
   */
  #toMoney(value, moneyFormat, currency) {
    return formatMoney(Math.round(value), moneyFormat, currency);
  }

  /**
   * Stack the thumb currently being interacted with on top of its sibling so it
   * stays grabbable even when the two thumbs sit close together / overlap.
   * @param {HTMLElement} sliderEl
   * @param {HTMLElement} activeThumb
   */
  #raiseActiveThumb(sliderEl, activeThumb) {
    sliderEl.querySelectorAll('.facets-price-slider__thumb').forEach((thumb) => {
      if (thumb instanceof HTMLElement) thumb.style.zIndex = thumb === activeThumb ? '5' : '4';
    });
  }

  /**
   * Reset both thumbs to the full range (min → 0, max → range max) and repaint.
   * Used when the Clear button blanks the range inputs (which would otherwise
   * leave them stranded at the track middle).
   * @param {HTMLElement} sliderEl
   */
  #resetSlider(sliderEl) {
    const thumbMin = /** @type {HTMLInputElement|null} */ (
      sliderEl.querySelector('.facets-price-slider__thumb--min')
    );
    const thumbMax = /** @type {HTMLInputElement|null} */ (
      sliderEl.querySelector('.facets-price-slider__thumb--max')
    );
    if (!thumbMin || !thumbMax) return;

    thumbMin.value = thumbMin.min || '0';
    thumbMax.value = sliderEl.dataset.rangeMax || thumbMax.max;
    thumbMin.style.zIndex = '';
    thumbMax.style.zIndex = '';
    this.#syncSliderUI(sliderEl);
  }

  /**
   * Mirror the current slider positions into the hidden text inputs expected by
   * price-facet-component (value + cross-clamp data attributes). Does NOT dispatch
   * an event — the range thumbs' own native `change` (on release) is what triggers
   * the engine's `updatePriceFilterAndResults`, and by then these inputs are fresh.
   * @param {HTMLElement} sliderEl
   */
  #syncSliderToInputs(sliderEl) {
    const thumbMin = /** @type {HTMLInputElement|null} */ (
      sliderEl.querySelector('.facets-price-slider__thumb--min')
    );
    const thumbMax = /** @type {HTMLInputElement|null} */ (
      sliderEl.querySelector('.facets-price-slider__thumb--max')
    );
    if (!thumbMin || !thumbMax) return;

    const panel = sliderEl.closest('.facets-drawer-panels__panel');
    const priceFacet = panel?.querySelector('price-facet-component');
    const minInput = /** @type {HTMLInputElement|null} */ (
      priceFacet?.querySelector('[ref="minInput"]')
    );
    const maxInput = /** @type {HTMLInputElement|null} */ (
      priceFacet?.querySelector('[ref="maxInput"]')
    );
    if (!minInput || !maxInput) return;

    const minVal = parseFloat(thumbMin.value);
    const maxVal = parseFloat(thumbMax.value);
    const currency = sliderEl.dataset.currency ?? 'USD';

    // Slider values are raw minor units. Convert to major-unit decimal using the
    // neutral {{amount}} format (period decimal, no thousands separator) so that
    // convertMoneyToMinorUnits in facets.js can reliably parse them back, and
    // Shopify's filter URL parameters receive the correct major-unit amount.
    const minFormatted = formatMoney(Math.round(minVal), '{{amount}}', currency);
    const maxFormatted = formatMoney(Math.round(maxVal), '{{amount}}', currency);

    minInput.value = minFormatted;
    maxInput.value = maxFormatted;

    // Update cross-clamp data attributes for the engine
    maxInput.setAttribute('data-min', minFormatted);
    minInput.setAttribute('data-max', maxFormatted);
  }
}

if (!customElements.get('facet-drawer-panels')) {
  customElements.define('facet-drawer-panels', FacetDrawerPanels);
}
